import {
    AssumptionSetStatus,
    type AssumptionSetCheck,
    type AssumptionSetState,
    type AssumptionSetSummary,
} from '@lightdash/common';
import {
    Alert,
    Badge,
    Button,
    Checkbox,
    Drawer,
    Group,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    Tooltip,
} from '@mantine/core';
import { useEffect, useMemo, useState, type FC } from 'react';
import MantineModal from '../../components/common/MantineModal';
import useApp from '../../providers/App/useApp';
import useDashboardContext from '../../providers/Dashboard/useDashboardContext';
import {
    useApproveAssumptionSet,
    useAssumptionSet,
    useAssumptionSets,
    useAssumptionSetState,
    usePublishAssumptionSet,
} from './useAssumptionSets';

/** A dashboard built from a stage preset says so in its description ("Stage: stage1"). */
export const stageOfDashboard = (description: string | undefined | null) =>
    /\bStage:\s*([a-z][a-z0-9_]*)\b/.exec(description ?? '')?.[1];

const COMPARISON: Record<string, string> = {
    none: '',
    same_inputs: 'matches the numbers on screen',
    inputs_differ: 'the assumptions on screen differ from it',
    tape_changed: 'a newer tape has been loaded since',
    rules_changed: 'the formulas have changed since',
};

const when = (d: Date | string | null) =>
    d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const statusText = (v: AssumptionSetSummary) =>
    v.status === AssumptionSetStatus.APPROVED
        ? `Version ${v.version} approved by ${v.approvedByName ?? '?'}, ${when(v.approvedAt)}`
        : v.status === AssumptionSetStatus.STALE
          ? `Version ${v.version} is out of date (published ${when(v.createdAt)})`
          : `Version ${v.version} published by ${v.createdByName ?? '?'}, ${when(v.createdAt)}: waiting for a second person to approve`;

/** The assumptions a version freezes, in words (the page's Window / Weighting / As of inputs). */
const WORDS: Record<string, string> = {
    full_book: 'Full book', trailing_12m: 'Trailing 12 months', orig_balance: 'Origination balance',
    current_balance: 'Current balance', loan_count: 'Loan count',
};
const describeInputs = (inputs: Record<string, string> | undefined) =>
    Object.entries(inputs ?? {})
        .filter(([k, v]) => !(['as_of', 'snapshot'].includes(k) && v === 'latest'))   // "latest tape" goes without saying
        .map(([k, v]) => `${k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}: ${WORDS[String(v)] ?? String(v).replace(/_/g, ' ')}`)
        .join(' · ') || 'the default assumptions';

/** Where an approved version's investor package lives (written by deploy/package.py on Approve). */
const packageHref = (dealId: string, version: number) => `${import.meta.env.BASE_URL ?? '/'}packages/${dealId}-v${version}.html`;

const fmtValue = (c: AssumptionSetCheck) =>
    c.value === null ? '—' : Number.isInteger(c.value) ? c.value.toLocaleString() : c.value.toLocaleString(undefined, { maximumFractionDigits: 1 });

const ChecksLine: FC<{ state: AssumptionSetState }> = ({ state }) => (
    <Group gap={6} data-testid="stage-checks">
        <Text size="xs" fw={600} c="dimmed">
            Checks
        </Text>
        {state.checks.map((c) => (
            <Tooltip key={c.id} label={`${c.id} · ${c.rule}`}>
                <Badge
                    size="sm"
                    variant="light"
                    color={c.passed ? 'gray' : c.severity === 'BLOCK' ? 'red' : 'yellow'}
                    data-check={c.id}
                    data-passed={c.passed}
                >
                    {c.passed ? '✓' : '✗'} {c.title}: {fmtValue(c)}
                </Badge>
            </Tooltip>
        ))}
        {state.review.map((r) => (
            <Badge key={r.id} size="sm" variant="light" color="yellow" data-check={r.id}>
                {r.title}: the approver confirms this
            </Badge>
        ))}
    </Group>
);

const VersionTables: FC<{ projectUuid: string; dealId: string; uuid: string }> = ({
    projectUuid,
    dealId,
    uuid,
}) => {
    const { data } = useAssumptionSet(projectUuid, dealId, uuid);
    if (!data) return <Text size="sm">Loading…</Text>;
    const download = () => {
        const blob = new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${dealId}-${data.module}-v${data.version}.json`;
        a.click();
    };
    return (
        <Stack gap="xs" data-testid="assumption-set-frozen">
            <Group justify="space-between">
                <Text size="sm" ff="monospace">
                    Assumptions: {describeInputs(data.inputs as Record<string, string>)} · tape run {String(data.ingestRunId).slice(0, 8)}
                </Text>
                <Button size="xs" variant="default" onClick={download}>
                    Download JSON
                </Button>
            </Group>
            {data.tables.map((t) => (
                <Stack key={t.name} gap={2}>
                    <Text size="sm" fw={600}>
                        {t.label}
                    </Text>
                    <Table withTableBorder fz="xs">
                        <Table.Thead>
                            <Table.Tr>
                                {Object.keys(t.rows[0] ?? {}).map((k) => (
                                    <Table.Th key={k}>{k}</Table.Th>
                                ))}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {t.rows.map((r, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Table.Tr key={i}>
                                    {Object.values(r).map((v, j) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Table.Td key={j}>{String(v)}</Table.Td>
                                    ))}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Stack>
            ))}
        </Stack>
    );
};

/** Publish / Approve / History for a deal's stage. Shown above stage-preset dashboards. */
const AssumptionSetBar: FC<{ projectUuid: string; module: string }> = ({
    projectUuid,
    module,
}) => {
    const { user } = useApp();
    const filters = useDashboardContext((c) => c.allFilters);
    const parameterValues = useDashboardContext((c) => c.parameterValues);
    const dealId = useMemo(() => {
        const f = filters?.dimensions?.find((d) => d.target.fieldId.endsWith('_deal_id'));
        const v = f?.values?.[0];
        return typeof v === 'string' ? v : undefined;
    }, [filters]);
    const inputs = useMemo(() => {
        const out: Record<string, string> = {};
        Object.entries(parameterValues ?? {}).forEach(([k, v]) => {
            if (k.startsWith(`${module}_`) && v !== undefined && v !== null && v !== '') {
                out[k.slice(module.length + 1)] = String(v);
            }
        });
        return out;
    }, [parameterValues, module]);
    const key = { projectUuid, dealId, module, inputs };
    const state = useAssumptionSetState(key);
    const publish = usePublishAssumptionSet(key);
    const approve = useApproveAssumptionSet(projectUuid, dealId);
    const [publishOpen, setPublishOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [opened, setOpened] = useState<string>();
    const [confirmed, setConfirmed] = useState<string[]>([]);
    const history = useAssumptionSets(projectUuid, dealId, module, true);   // also the fast source of the status
    // Publishing takes minutes; the workspace proxy may drop the request while the server goes on.
    // So the bar shows "Publishing…" and checks every 10 s until the new version is there.
    const [publishingFrom, setPublishingFrom] = useState<number | null>(null);
    const latestVersion = state.data?.latest?.version ?? 0;
    const publishing = publishingFrom !== null && latestVersion <= publishingFrom;
    useEffect(() => {
        if (!publishing) return undefined;
        const t = setInterval(() => void state.refetch(), 10000);
        return () => clearInterval(t);
    }, [publishing, state]);

    if (!dealId) {
        return (
            <Paper withBorder p="xs" mx="lg" mt="xs" data-testid="assumption-set-bar">
                <Text size="sm">Choose a deal in the Deal filter to publish its {module} numbers.</Text>
            </Paper>
        );
    }
    const s = state.data;
    const latest = s?.latest ?? history.data?.[0] ?? null;
    const draft = latest?.status === AssumptionSetStatus.DRAFT ? latest : null;
    const own = draft && draft.createdByUserUuid === user.data?.userUuid;
    const blockers = s?.checks.filter((c) => c.severity === 'BLOCK' && !c.passed) ?? [];
    // a failed state call must be visible, not look like "nothing published"
    const rawError = (state.error ?? publish.error ?? approve.error)?.error?.message;
    // a dropped connection while publishing is not a failure: the server finishes the version
    const error = publishing && rawError?.includes('unable to reach the server') ? undefined : rawError;
    const approved = latest?.status === AssumptionSetStatus.APPROVED ? latest : null;

    return (
        <Paper
            withBorder
            p="xs"
            mx="lg"
            mt="xs"
            data-testid="assumption-set-bar"
            data-blocked={s?.blocked ?? false}
            style={s?.blocked ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}
        >
            <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" data-testid="assumption-set-status">
                        {publishing && <b>Publishing version {publishingFrom! + 1}… this takes a few minutes. </b>}
                        {!publishing && !latest && (history.isLoading ? 'Looking up published versions…' : 'Nothing published yet: the numbers on screen are live.')}
                        {!publishing && latest && statusText(latest)}
                        {!publishing && state.isLoading && ' Checking the numbers before you can publish…'}
                        {!publishing && latest && COMPARISON[s?.comparison ?? 'none']
                            ? `; ${COMPARISON[s?.comparison ?? 'none']}.`
                            : ''}
                    </Text>
                    {approved && dealId && (
                        <Button size="xs" variant="light" component="a" href={packageHref(dealId, approved.version)}
                            target="_blank" data-testid="investor-package-link">
                            Investor package (version {approved.version})
                        </Button>
                    )}
                </Group>
                {s?.blocked && (
                    <Alert color="red" p={6} data-testid="assumption-set-blocked">
                        These numbers cannot be published: {blockers.map((c) => c.title).join('; ')}
                    </Alert>
                )}
                {error && (
                    <Alert color="red" p={6} data-testid="assumption-set-error">
                        {error}
                    </Alert>
                )}
                <Group gap="xs">
                    <Button
                        size="xs"
                        data-testid="publish-assumption-set"
                        disabled={!s || s.blocked || !s.canPublish || publishing}
                        loading={publishing}
                        onClick={() => setPublishOpen(true)}
                    >
                        Publish these numbers
                    </Button>
                    <Tooltip
                        label={own ? 'Four eyes: a different user approves' : 'Approve the latest draft'}
                        disabled={!draft}
                    >
                        <Button
                            size="xs"
                            variant="default"
                            data-testid="approve-assumption-set"
                            disabled={!draft || !!own || !s?.canApprove}
                            onClick={() => {
                                setConfirmed([]);
                                setApproveOpen(true);
                            }}
                        >
                            Approve
                        </Button>
                    </Tooltip>
                    <Button size="xs" variant="default" data-testid="assumption-set-history" onClick={() => setHistoryOpen(true)}>
                        History
                    </Button>
                </Group>
                {s && <ChecksLine state={s} />}
            </Stack>

            <MantineModal
                opened={publishOpen}
                onClose={() => setPublishOpen(false)}
                title={`Publish version ${latestVersion + 1}`}
                size="lg"
            >
                <Stack gap="xs" data-testid="publish-dialog">
                    <Text size="sm">
                        Freezes today's numbers on these pages as version {latestVersion + 1}, with the
                        assumptions on screen ({describeInputs(inputs)}) and the current tape. Nothing changes on
                        screen. A second person then approves it; after approval the investor package for this
                        version is ready as a link here.
                    </Text>
                    {s && <ChecksLine state={s} />}
                    <Group justify="flex-end">
                        <Button
                            data-testid="publish-confirm"
                            onClick={() => {
                                setPublishingFrom(latestVersion);
                                setPublishOpen(false);
                                publish.mutate(undefined, { onSettled: () => void state.refetch() });
                            }}
                        >
                            Publish version {latestVersion + 1}
                        </Button>
                    </Group>
                </Stack>
            </MantineModal>

            <MantineModal
                opened={approveOpen}
                onClose={() => setApproveOpen(false)}
                title={draft ? `Approve assumption set v${draft.version}` : 'Approve'}
                size="lg"
            >
                <Stack gap="xs" data-testid="approve-dialog">
                    {draft && (
                        <Text size="sm">
                            Published by {draft.createdByName}, {when(draft.createdAt)}, with{' '}
                            {describeInputs(draft.inputs as Record<string, string>)}. Approving freezes it for
                            investors: their package is built from exactly these numbers.
                        </Text>
                    )}
                    {s?.review.map((r) => (
                        <Checkbox
                            key={r.id}
                            label={`${r.title} (${r.id})`}
                            data-testid={`review-${r.id}`}
                            checked={confirmed.includes(r.id)}
                            onChange={(e) =>
                                setConfirmed((c) =>
                                    e.currentTarget.checked ? [...c, r.id] : c.filter((x) => x !== r.id),
                                )
                            }
                        />
                    ))}
                    <Group justify="flex-end">
                        <Button
                            data-testid="approve-confirm"
                            loading={approve.isLoading}
                            disabled={!draft || (s?.review.length ?? 0) !== confirmed.length}
                            onClick={() =>
                                draft &&
                                approve.mutate(
                                    { uuid: draft.assumptionSetUuid, reviewConfirmed: confirmed },
                                    { onSettled: () => setApproveOpen(false) },
                                )
                            }
                        >
                            Approve
                        </Button>
                    </Group>
                </Stack>
            </MantineModal>

            <Drawer
                opened={historyOpen}
                onClose={() => setHistoryOpen(false)}
                position="right"
                size="xl"
                title="Published versions of these numbers"
            >
                <ScrollArea h="85vh">
                    <Table data-testid="assumption-set-history-table" fz="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Version</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Assumptions</Table.Th>
                                <Table.Th>Investor package</Table.Th>
                                <Table.Th>Published</Table.Th>
                                <Table.Th>Approved</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {(history.data ?? []).map((v) => (
                                <Table.Tr
                                    key={v.assumptionSetUuid}
                                    onClick={() => setOpened(v.assumptionSetUuid)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Table.Td>v{v.version}</Table.Td>
                                    <Table.Td>{v.status === AssumptionSetStatus.APPROVED ? 'Approved' : v.status === AssumptionSetStatus.STALE ? 'Out of date' : 'Waiting for approval'}</Table.Td>
                                    <Table.Td>{describeInputs(v.inputs as Record<string, string>)}</Table.Td>
                                    <Table.Td>
                                        {v.status === AssumptionSetStatus.APPROVED && dealId ? (
                                            <a href={packageHref(dealId, v.version)} target="_blank" rel="noreferrer">
                                                Open package
                                            </a>
                                        ) : (
                                            '—'
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        {v.createdByName} · {when(v.createdAt)}
                                    </Table.Td>
                                    <Table.Td>
                                        {v.approvedByName ? `${v.approvedByName} · ${when(v.approvedAt)}` : ''}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    {opened && <VersionTables projectUuid={projectUuid} dealId={dealId} uuid={opened} />}
                </ScrollArea>
            </Drawer>
        </Paper>
    );
};

export default AssumptionSetBar;
